import { FC, Suspense, useState } from 'react'

import { Auth0Provider } from '@auth0/auth0-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CatchBoundary } from '@tanstack/react-router'

import { ToastProvider } from '@forto-dev/phoenix-ui-core'

import { getAuth0Config, getConfig } from '@/app/config.ts'
import { AnalyticsContext, AnalyticsService } from '@/utils/analytics'
import { warn } from '@/utils/logger'

import { AuthLoader } from './features/auth/AuthLoader'
import { AppSkeleton } from './fragments/appSkeleton'
import { createAppErrorFallback } from './fragments/create-error-fallback'
import { Router } from './fragments/router'
import { GlobalLoaders } from './utils/globalLoaders/GlobalLoaders'
import { LocalisationLoader } from './utils/localisation/LocalisationLoader'

import './Window'
import './utils/userlane/UserlaneService'

const analyticsId = getConfig().FULLSTORY_ANALYTICS_ID ?? ''
if (!analyticsId) {
  warn('FullStory Analytics ID is not configured')
}
const analyticsService = new AnalyticsService(analyticsId)

const auth0Config = getAuth0Config()

export const App: FC = () => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        /**
         * Copied default options from frontend-infra to avoid a dependency: https://github.com/freight-hub/frontend-infra/blob/main/libs/reactquery/src/defaultOptions.ts
         * normal defaults: https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults#important-defaults
         */
        defaultOptions: {
          queries: {
            // retries 3 times by default which also blocks the UI until successful response is received
            // we rather want to fail fast
            retry: false,
            // only makes sense for products where data changes in seconds
            // refetchOnWindowFocus: false, // keeping this commented out for now, as it might be useful for some features
            // by default react query waits for network to be online before making a request, which is buggy in some browsers
            // since we don't have offline usecase, we communicate we always expect network to be online
            networkMode: 'always',
          },
        },
      })
  )

  return (
    <Auth0Provider {...auth0Config}>
      <QueryClientProvider client={queryClient}>
        <CatchBoundary getResetKey={() => 1} errorComponent={createAppErrorFallback()}>
          <Suspense fallback={<AppSkeleton />}>
            <AnalyticsContext.Provider value={analyticsService}>
              <ToastProvider>
                <GlobalLoaders loaders={[AuthLoader, LocalisationLoader]} />
                <Router />
              </ToastProvider>
            </AnalyticsContext.Provider>
          </Suspense>
        </CatchBoundary>
      </QueryClientProvider>
    </Auth0Provider>
  )
}
